import React, { useEffect, useState } from 'react';
import { apiFetch } from '../lib/api';
import { DefaultCnAccountPanel } from '../components/CnAccountPicker'; // @@CN_DEFAULT_v109@@
import { PaymentSettingsPanel } from '../components/PortalPayments'; // @@PORTAL_PAY_v124_1@@

// @@SETTINGS_EDITABLE_v1@@
// Portal settings. Currently: outbound shipping options (label-only presets)
// used by production jobs. Admin/manager only (route-guarded in App.js).
// Options can be added, renamed (inline), reordered (up/down), and deleted.
// The server PATCH /:id accepts { label, sort_order }; reorder swaps the
// sort_order of two adjacent rows. List is shown in sort_order order.
export default function Settings() {
  const [options, setOptions] = useState([]);
  const [label, setLabel] = useState('');
  const [msg, setMsg] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState('');
  // @@FEDEX_LABELS_v60p2@@ shipping config (default From/To + parcel for FedEx labels)
  const [ship, setShip] = useState(null);
  const [shipMsg, setShipMsg] = useState('');
  const [shipSaving, setShipSaving] = useState(false);
  // @@PARCEL_PRESETS_v63p2@@ parcel size presets
  const [presets, setPresets] = useState([]);
  const [presetForm, setPresetForm] = useState({ name: '', length_in: '', width_in: '', height_in: '', weight_lbs: '' });
  const [presetMsg, setPresetMsg] = useState('');
  // @@APP_SETTINGS_v1@@ global settings (archive TTL)
  const [ttl, setTtl] = useState('');
  const [ttlMsg, setTtlMsg] = useState('');
  const [ttlSaving, setTtlSaving] = useState(false);

  // @@STATUS_BANNER_v78@@ storefront status banner (served at /pub-api/status)
  const [banner, setBanner] = useState({ status_banner_active: false, status_banner_message: '', status_banner_link_url: '', status_banner_link_label: '' });
  const [bannerMsg, setBannerMsg] = useState('');
  const [bannerSaving, setBannerSaving] = useState(false);
  // @@SSO_MFA_v126@@ sign-in security (Google required for staff)
  const [signin, setSignin] = useState({ required: false, available: false });
  const [signinMsg, setSigninMsg] = useState('');
  const [signinSaving, setSigninSaving] = useState(false);

  async function loadBanner() {
    const res = await apiFetch('/api/settings');
    if (res.ok) {
      const d = await res.json();
      const s = d.settings || {};
      setBanner({
        status_banner_active: s.status_banner_active === true,
        status_banner_message: s.status_banner_message || '',
        status_banner_link_url: s.status_banner_link_url || '',
        status_banner_link_label: s.status_banner_link_label || '',
      });
      setSignin({ required: s.staff_google_required === true, available: s.google_available === true }); // @@SSO_MFA_v126@@
    }
  }
  useEffect(() => { loadBanner(); }, []);

  function bannerField(k, v) { setBanner((c) => ({ ...c, [k]: v })); }

  // @@SSO_MFA_v126@@
  async function saveSignin(required) {
    setSigninMsg(''); setSigninSaving(true);
    try {
      const res = await apiFetch('/api/settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ staff_google_required: required }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { setSigninMsg(d.error || 'Could not save.'); return; }
      const st = d.settings || {};
      setSignin({ required: st.staff_google_required === true, available: st.google_available === true });
      setSigninMsg(required ? 'Staff must now sign in with Google.' : 'Staff may sign in with a password again.');
    } catch (e) { setSigninMsg('Could not reach the server.'); }
    finally { setSigninSaving(false); }
  }

  async function saveBanner() {
    setBannerMsg(''); setBannerSaving(true);
    try {
      const res = await apiFetch('/api/settings', {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(banner),
      });
      if (res.ok) {
        const d = await res.json();
        const s = d.settings || {};
        setBanner({
          status_banner_active: s.status_banner_active === true,
          status_banner_message: s.status_banner_message || '',
          status_banner_link_url: s.status_banner_link_url || '',
          status_banner_link_label: s.status_banner_link_label || '',
        });
        setBannerMsg(s.status_banner_active ? 'Saved \u2014 live on all stores within a minute.' : 'Saved \u2014 banner is off.');
      } else {
        const d = await res.json().catch(() => ({}));
        setBannerMsg(d.error || 'Could not save.');
      }
    } finally { setBannerSaving(false); }
  }

  async function load() {
    const res = await apiFetch('/api/shipping-options');
    if (res.ok) { const d = await res.json(); setOptions(d.options || []); }
  }
  useEffect(() => { load(); }, []);

  // @@FEDEX_LABELS_v60p2@@
  async function loadShip() {
    const res = await apiFetch('/api/packages/shipping-config');
    if (res.ok) { const d = await res.json(); setShip(d.config || null); }
  }
  useEffect(() => { loadShip(); }, []);
  function shipField(k, v) { setShip((c) => ({ ...(c || {}), [k]: v })); }
  async function saveShip() {
    setShipMsg(''); setShipSaving(true);
    try {
      const res = await apiFetch('/api/packages/shipping-config', {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(ship || {}),
      });
      if (res.ok) { const d = await res.json(); setShip(d.config); setShipMsg('Saved.'); }
      else { const d = await res.json().catch(() => ({})); setShipMsg(d.error || 'Could not save.'); }
    } finally { setShipSaving(false); }
  }

  // @@PARCEL_PRESETS_v63p2@@
  async function loadPresets() {
    const res = await apiFetch('/api/packages/parcel-presets');
    if (res.ok) { const d = await res.json(); setPresets(d.presets || []); }
  }
  useEffect(() => { loadPresets(); }, []);
  async function addPreset() {
    setPresetMsg('');
    const name = presetForm.name.trim();
    if (!name) { setPresetMsg('Name is required.'); return; }
    const res = await apiFetch('/api/packages/parcel-presets', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...presetForm, name, sort_order: presets.length }),
    });
    if (res.ok) { setPresetForm({ name: '', length_in: '', width_in: '', height_in: '', weight_lbs: '' }); loadPresets(); }
    else { const d = await res.json().catch(() => ({})); setPresetMsg(d.error || 'Could not add.'); }
  }
  async function removePreset(id) {
    if (!window.confirm('Delete this parcel preset?')) return;
    const res = await apiFetch(`/api/packages/parcel-presets/${id}`, { method: 'DELETE' });
    if (res.ok) loadPresets();
  }

  async function add() {
    setMsg('');
    const name = label.trim();
    if (!name) return;
    const res = await apiFetch('/api/shipping-options', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ label: name, sort_order: options.length }),
    });
    if (res.ok) { setLabel(''); load(); }
    else { const d = await res.json().catch(() => ({})); setMsg(d.error || 'Could not add.'); }
  }

  async function remove(id) {
    if (!window.confirm('Delete this shipping option? Existing shipments keep their label.')) return;
    const res = await apiFetch(`/api/shipping-options/${id}`, { method: 'DELETE' });
    if (res.ok) load();
  }

  function startEdit(o) { setEditingId(o.id); setDraft(o.label); setMsg(''); }
  function cancelEdit() { setEditingId(null); setDraft(''); }

  async function saveEdit(id) {
    const name = draft.trim();
    if (!name) { setMsg('Label cannot be empty.'); return; }
    const res = await apiFetch(`/api/shipping-options/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ label: name }),
    });
    if (res.ok) { cancelEdit(); load(); }
    else { const d = await res.json().catch(() => ({})); setMsg(d.error || 'Could not rename.'); }
  }

  // Swap the sort_order of the option at idx with its neighbor (dir -1 up / +1 down).
  async function move(idx, dir) {
    const j = idx + dir;
    if (j < 0 || j >= options.length) return;
    const a = options[idx];
    const b = options[j];
    setMsg('');
    // Two PATCHes: give each the other's sort_order. Fall back to index-based
    // values if sort_order is missing/equal so the swap still takes effect.
    const aSort = Number.isFinite(Number(a.sort_order)) ? Number(a.sort_order) : idx;
    const bSort = Number.isFinite(Number(b.sort_order)) ? Number(b.sort_order) : j;
    const sortA = aSort === bSort ? j : bSort;
    const sortB = aSort === bSort ? idx : aSort;
    const r1 = await apiFetch(`/api/shipping-options/${a.id}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sort_order: sortA }),
    });
    const r2 = await apiFetch(`/api/shipping-options/${b.id}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sort_order: sortB }),
    });
    if (r1.ok && r2.ok) load();
    else setMsg('Could not reorder.');
  }

  const arrowBtn = {
    width: 'auto', padding: '0 10px', marginRight: 4, lineHeight: 1,
  };

  // @@APP_SETTINGS_v1@@
  useEffect(() => {
    (async () => {
      const res = await apiFetch('/api/settings');
      if (res.ok) { const d = await res.json(); setTtl(String(d.settings?.archive_ttl_days ?? '')); }
    })();
  }, []);
  async function saveTtl() {
    setTtlMsg(''); setTtlSaving(true);
    try {
      const n = Number(ttl);
      if (!Number.isInteger(n) || n < 1 || n > 365) { setTtlMsg('Enter a whole number of days between 1 and 365.'); return; }
      const res = await apiFetch('/api/settings', {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ archive_ttl_days: n }),
      });
      if (res.ok) { const d = await res.json(); setTtl(String(d.settings.archive_ttl_days)); setTtlMsg('Saved.'); }
      else { const d = await res.json().catch(() => ({})); setTtlMsg(d.error || 'Could not save.'); }
    } finally { setTtlSaving(false); }
  }

  return (
    <div className="hq-page-content">
      <div className="hq-page-head">
        <h1>Settings</h1>
        <p>Portal configuration.</p>
      </div>

      <div className="hq-panel" style={{ marginBottom: 20 }}>
        <div className="hq-panel-head">
          <h2>Production</h2>
          <button className="hq-btn" onClick={saveTtl} disabled={ttlSaving}>{ttlSaving ? 'Saving\u2026' : 'Save'}</button>
        </div>
        <div style={{ padding: 20 }}>
          <label style={{ display: 'block', fontSize: 13, color: 'var(--hq-muted)', marginBottom: 8 }}>
            Auto-archive completed jobs after this many days
          </label>
          <div className="hq-form">
            <input className="hq-input" type="number" min="1" max="365" value={ttl}
              onChange={(e) => setTtl(e.target.value)} style={{ maxWidth: 120 }} />
            <span style={{ color: 'var(--hq-muted)', fontSize: 13 }}>days (1\u2013365)</span>
          </div>
          {ttlMsg && <p className="hq-msg">{ttlMsg}</p>}
        </div>
      </div>

      <div className="hq-panel">
        <div className="hq-panel-head"><h2>Outbound shipping options</h2></div>
        <div style={{ padding: 20 }}>
          <div className="hq-form">
            <input className="hq-input" placeholder="e.g. FedEx Ground" value={label}
              onChange={(e) => setLabel(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') add(); }} style={{ minWidth: 240 }} />
            <button className="hq-btn" onClick={add}>Add option</button>
          </div>
          {msg && <p className="hq-msg">{msg}</p>}
        </div>
        <table className="hq-table">
          <thead><tr><th>Label</th><th style={{ width: 220 }}></th></tr></thead>
          <tbody>
            {options.map((o, idx) => (
              <tr key={o.id}>
                <td>
                  {editingId === o.id ? (
                    <input className="hq-input" value={draft} autoFocus
                      onChange={(e) => setDraft(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(o.id); if (e.key === 'Escape') cancelEdit(); }}
                      style={{ minWidth: 200 }} />
                  ) : (
                    o.label
                  )}
                </td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {editingId === o.id ? (
                    <>
                      <button className="hq-btn" style={{ width: 'auto', padding: '0 12px', marginRight: 6 }}
                        onClick={() => saveEdit(o.id)}>Save</button>
                      <button className="hq-signout" style={{ width: 'auto', padding: '0 12px' }}
                        onClick={cancelEdit}>Cancel</button>
                    </>
                  ) : (
                    <>
                      <button className="hq-signout" style={arrowBtn} disabled={idx === 0}
                        onClick={() => move(idx, -1)} title="Move up">↑</button>
                      <button className="hq-signout" style={arrowBtn} disabled={idx === options.length - 1}
                        onClick={() => move(idx, 1)} title="Move down">↓</button>
                      <button className="hq-signout" style={{ width: 'auto', padding: '0 12px', marginRight: 6 }}
                        onClick={() => startEdit(o)}>Edit</button>
                      <button className="hq-signout" style={{ width: 'auto', padding: '0 12px', borderColor: 'rgba(255,90,82,.3)', color: '#ff5a52' }}
                        onClick={() => remove(o.id)}>Delete</button>
                    </>
                  )}
                </td>
              </tr>
            ))}
            {options.length === 0 && (
              <tr><td className="hq-empty" colSpan={2}>No shipping options yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* @@FEDEX_LABELS_v60p2@@ default From/To + parcel for FedEx label creation */}
      <div className="hq-panel" style={{ marginTop: 20 }}>
        <div className="hq-panel-head">
          <h2>Shipping (FedEx labels)</h2>
          <button className="hq-btn" onClick={saveShip} disabled={shipSaving || !ship}>{shipSaving ? 'Saving…' : 'Save'}</button>
        </div>
        <div style={{ padding: 20 }}>
          {!ship ? (
            <p className="hq-empty" style={{ padding: 0 }}>Loading…</p>
          ) : (
            <>
              <h3 style={{ margin: '0 0 10px', fontSize: 13, color: 'var(--hq-muted)', textTransform: 'uppercase', letterSpacing: '.08em' }}>Ship from</h3>
              <div className="hq-form" style={{ marginBottom: 18 }}>
                <input className="hq-input" placeholder="Name" value={ship.from_name || ''} onChange={(e) => shipField('from_name', e.target.value)} />
                <input className="hq-input" placeholder="Phone" value={ship.from_phone || ''} onChange={(e) => shipField('from_phone', e.target.value)} />
                <input className="hq-input" placeholder="Street" value={ship.from_street1 || ''} onChange={(e) => shipField('from_street1', e.target.value)} style={{ minWidth: 240 }} />
                <input className="hq-input" placeholder="Street 2 (optional)" value={ship.from_street2 || ''} onChange={(e) => shipField('from_street2', e.target.value)} />
                <input className="hq-input" placeholder="City" value={ship.from_city || ''} onChange={(e) => shipField('from_city', e.target.value)} />
                <input className="hq-input" placeholder="State" value={ship.from_state || ''} onChange={(e) => shipField('from_state', e.target.value)} style={{ maxWidth: 90 }} />
                <input className="hq-input" placeholder="ZIP" value={ship.from_postal || ''} onChange={(e) => shipField('from_postal', e.target.value)} style={{ maxWidth: 120 }} />
                <input className="hq-input" placeholder="Country" value={ship.from_country || ''} onChange={(e) => shipField('from_country', e.target.value)} style={{ maxWidth: 90 }} />
              </div>
              <h3 style={{ margin: '0 0 10px', fontSize: 13, color: 'var(--hq-muted)', textTransform: 'uppercase', letterSpacing: '.08em' }}>Ship to (default for all labels)</h3>
              <div className="hq-form" style={{ marginBottom: 18 }}>
                <input className="hq-input" placeholder="Name" value={ship.to_name || ''} onChange={(e) => shipField('to_name', e.target.value)} />
                <input className="hq-input" placeholder="Phone" value={ship.to_phone || ''} onChange={(e) => shipField('to_phone', e.target.value)} />
                <input className="hq-input" placeholder="Street" value={ship.to_street1 || ''} onChange={(e) => shipField('to_street1', e.target.value)} style={{ minWidth: 240 }} />
                <input className="hq-input" placeholder="Street 2 (optional)" value={ship.to_street2 || ''} onChange={(e) => shipField('to_street2', e.target.value)} />
                <input className="hq-input" placeholder="City" value={ship.to_city || ''} onChange={(e) => shipField('to_city', e.target.value)} />
                <input className="hq-input" placeholder="State" value={ship.to_state || ''} onChange={(e) => shipField('to_state', e.target.value)} style={{ maxWidth: 90 }} />
                <input className="hq-input" placeholder="ZIP" value={ship.to_postal || ''} onChange={(e) => shipField('to_postal', e.target.value)} style={{ maxWidth: 120 }} />
                <input className="hq-input" placeholder="Country" value={ship.to_country || ''} onChange={(e) => shipField('to_country', e.target.value)} style={{ maxWidth: 90 }} />
              </div>
              <h3 style={{ margin: '0 0 10px', fontSize: 13, color: 'var(--hq-muted)', textTransform: 'uppercase', letterSpacing: '.08em' }}>Default parcel</h3>
              <div className="hq-form">
                <input className="hq-input" placeholder="Weight (lb)" value={ship.default_weight_lbs == null ? '' : ship.default_weight_lbs} onChange={(e) => shipField('default_weight_lbs', e.target.value)} style={{ maxWidth: 130 }} />
                <input className="hq-input" placeholder="Length (in)" value={ship.default_length_in == null ? '' : ship.default_length_in} onChange={(e) => shipField('default_length_in', e.target.value)} style={{ maxWidth: 130 }} />
                <input className="hq-input" placeholder="Width (in)" value={ship.default_width_in == null ? '' : ship.default_width_in} onChange={(e) => shipField('default_width_in', e.target.value)} style={{ maxWidth: 130 }} />
                <input className="hq-input" placeholder="Height (in)" value={ship.default_height_in == null ? '' : ship.default_height_in} onChange={(e) => shipField('default_height_in', e.target.value)} style={{ maxWidth: 130 }} />
                <input className="hq-input" placeholder="Service (e.g. FEDEX_GROUND)" value={ship.default_service || ''} onChange={(e) => shipField('default_service', e.target.value)} style={{ minWidth: 220 }} />
              </div>
              {shipMsg && <p className="hq-msg">{shipMsg}</p>}
            </>
          )}
        </div>
      </div>

      {/* @@PARCEL_PRESETS_v63p2@@ reusable parcel sizes for the label form */}
      <div className="hq-panel" style={{ marginTop: 20 }}>
        <div className="hq-panel-head"><h2>Parcel presets</h2></div>
        <div style={{ padding: 20 }}>
          <div className="hq-form">
            <input className="hq-input" placeholder="Name (e.g. Box of T-Shirts)" value={presetForm.name}
              onChange={(e) => setPresetForm({ ...presetForm, name: e.target.value })} style={{ minWidth: 220 }} />
            <input className="hq-input" placeholder="L in" value={presetForm.length_in}
              onChange={(e) => setPresetForm({ ...presetForm, length_in: e.target.value })} style={{ maxWidth: 80 }} />
            <input className="hq-input" placeholder="W in" value={presetForm.width_in}
              onChange={(e) => setPresetForm({ ...presetForm, width_in: e.target.value })} style={{ maxWidth: 80 }} />
            <input className="hq-input" placeholder="H in" value={presetForm.height_in}
              onChange={(e) => setPresetForm({ ...presetForm, height_in: e.target.value })} style={{ maxWidth: 80 }} />
            <input className="hq-input" placeholder="Weight lb" value={presetForm.weight_lbs}
              onChange={(e) => setPresetForm({ ...presetForm, weight_lbs: e.target.value })} style={{ maxWidth: 100 }} />
            <button className="hq-btn" onClick={addPreset}>Add preset</button>
          </div>
          {presetMsg && <p className="hq-msg">{presetMsg}</p>}
        </div>
        <table className="hq-table">
          <thead><tr><th>Name</th><th>L×W×H (in)</th><th>Weight (lb)</th><th style={{ width: 100 }}></th></tr></thead>
          <tbody>
            {presets.map((p) => (
              <tr key={p.id}>
                <td>{p.name}</td>
                <td>{[p.length_in, p.width_in, p.height_in].map((n) => (n == null ? '—' : n)).join(' × ')}</td>
                <td>{p.weight_lbs == null ? '—' : p.weight_lbs}</td>
                <td style={{ textAlign: 'right' }}>
                  <button className="hq-signout" style={{ width: 'auto', padding: '0 12px', borderColor: 'rgba(255,90,82,.3)', color: '#ff5a52' }}
                    onClick={() => removePreset(p.id)}>Delete</button>
                </td>
              </tr>
            ))}
            {presets.length === 0 && (
              <tr><td className="hq-empty" colSpan={4}>No parcel presets yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {/* @@SSO_MFA_v126@@ sign-in security */}
      <div className="hq-panel" style={{ marginTop: 20 }}>
        <div className="hq-panel-head"><h2>Sign-in Security</h2></div>
        <div style={{ padding: 20 }}>
          <p style={{ color: 'var(--hq-muted)', fontSize: 13, margin: '0 0 14px' }}>
            Staff can sign in with their @xyzmerch.com Google account (Google Workspace enforces 2-step
            verification there). Client logins get a 6-digit code by email unless they’ve connected a Google
            account or trusted their device. Turning this on blocks password sign-in for all internal roles —
            you must have signed in with Google yourself at least once.
          </p>
          <p style={{ fontSize: 13, margin: '0 0 14px' }}>
            Google sign-in: <span className={signin.available ? 'hq-pill hq-on' : 'hq-pill hq-off'}>{signin.available ? 'configured' : 'not configured'}</span>
            {!signin.available && <span style={{ color: 'var(--hq-muted)', marginLeft: 8 }}>Set GOOGLE_CLIENT_ID / GOOGLE_CLIENT_SECRET on the web service.</span>}
          </p>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
            <input type="checkbox" checked={signin.required} disabled={signinSaving || (!signin.available && !signin.required)}
              onChange={(e) => saveSignin(e.target.checked)} />
            Require Google sign-in for staff
          </label>
          {signinMsg && <p className="hq-msg">{signinMsg}</p>}
        </div>
      </div>
      {/* @@CN_DEFAULT_v109@@ default Zoho credit-note account (store reports + POS) */}
      <DefaultCnAccountPanel />
      {/* @@STATUS_BANNER_v78@@ storefront status banner */}
      <div className="hq-panel" style={{ marginTop: 20 }}>
        <div className="hq-panel-head">
          <h2>Store Status Banner</h2>
          <button className="hq-btn" onClick={saveBanner} disabled={bannerSaving}>{bannerSaving ? 'Saving\u2026' : 'Save'}</button>
        </div>
        <div style={{ padding: 20 }}>
          <p style={{ color: 'var(--hq-muted)', fontSize: 13, margin: '0 0 14px' }}>
            Shows at the very top of every client storefront that has the XYZ Status Banner
            section installed. One message, all stores. Turn it off here and it disappears
            everywhere within a minute.
          </p>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, fontSize: 14 }}>
            <input type="checkbox" checked={banner.status_banner_active}
              onChange={(e) => bannerField('status_banner_active', e.target.checked)} />
            Active
          </label>
          <div className="hq-form">
            <input className="hq-input" placeholder="Message" maxLength={300}
              style={{ minWidth: 360, flex: 1 }} value={banner.status_banner_message}
              onChange={(e) => bannerField('status_banner_message', e.target.value)} />
            <input className="hq-input" placeholder="Link URL (optional)"
              style={{ minWidth: 260 }} value={banner.status_banner_link_url}
              onChange={(e) => bannerField('status_banner_link_url', e.target.value)} />
            <input className="hq-input" placeholder="Link text (optional)" maxLength={60}
              style={{ minWidth: 180 }} value={banner.status_banner_link_label}
              onChange={(e) => bannerField('status_banner_link_label', e.target.value)} />
          </div>
          <div style={{ marginTop: 16, fontSize: 12, color: 'var(--hq-muted)', textTransform: 'uppercase', letterSpacing: '.08em' }}>Preview</div>
          <div style={{ marginTop: 8, background: '#0a0a0b', color: '#fff', borderRadius: 8, padding: '10px 16px', textAlign: 'center', fontSize: 14, opacity: banner.status_banner_active ? 1 : 0.4 }}>
            <span>{banner.status_banner_message || 'Your message will appear here.'}</span>
            {banner.status_banner_link_url ? (
              <span style={{ color: '#31b44b', fontWeight: 600, marginLeft: 10, textDecoration: 'underline' }}>
                {banner.status_banner_link_label || 'Learn more'}
              </span>
            ) : null}
          </div>
          {bannerMsg && <p className="hq-msg">{bannerMsg}</p>}
        </div>
      </div>

      <PaymentSettingsPanel /> {/* @@PORTAL_PAY_v124_1@@ client portal payments: card, fee, bank, Wise, check */}
    </div>
  );
}
